import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { useCallback, useState } from "react";
import { apiOSS } from "../../../../api/apiOSS";
import { isSeries, SidePanelData } from "../types/side-panel-data";
import {
  buildSeriesResourceContext,
  buildSingleResourceContext,
} from "../utils/build-series-resource-context";

export type AnalysisState = {
  analysis?: string;
  loading: boolean;
  error?: string;
};

type AnalysisCacheEntry = AnalysisState & {
  resourceContext: string;
};

/**
 * Generates a unique identifier for cache keys based on table type and row ID.
 */
function buildCacheKey(tableType: string, rowId: string): string {
  return `${tableType}-${rowId}`;
}

/**
 * Custom hook to manage AI analysis caching for resources.
 * Prevents race conditions by storing analyses by unique resource ID.
 * Cache persists for the entire session (until page refresh).
 */
export function useResourceAnalysisCache({
  bundle,
  isEnabled = false,
}: {
  bundle: FhirBundleSdk | undefined;
  isEnabled?: boolean;
}) {
  const [cache, setCache] = useState<Record<string, AnalysisCacheEntry>>({});

  /**
   * Builds resource context for a given data object
   */
  const buildResourceContext = useCallback(
    (data: SidePanelData<unknown>): string => {
      if (!bundle) return "";

      if (isSeries(data)) {
        return buildSeriesResourceContext(data.series, bundle);
      } else {
        return buildSingleResourceContext(data.resource, bundle);
      }
    },
    [bundle]
  );

  /**
   * Extracts readable display names from data
   */
  const extractRowReadableNames = useCallback((data: SidePanelData<unknown>): string[] => {
    if (isSeries(data)) {
      // For series, use the series display name or code display
      const displayName = data.series.codeDisplay || data.series.resourceType || "Unknown";
      return [displayName];
    } else {
      // For single resources, use the metadata display name
      return [data.metadata.displayName];
    }
  }, []);

  /**
   * Starts analysis for a given data object if not already cached.
   * Updates cache with loading state, then fetches from API.
   */
  const startAnalysis = useCallback(
    async ({
      data,
      rowData,
      tableType,
      rowId,
      suspectsContext,
    }: {
      data: SidePanelData<unknown>;
      rowData: unknown;
      tableType: string;
      rowId: string;
      suspectsContext?: string;
    }) => {
      if (!bundle || !isEnabled) return;

      const uuid = buildCacheKey(tableType, rowId);

      // Check if already cached - must check synchronously
      if (cache[uuid]) {
        return;
      }

      // Build context for this resource
      const resourceContext = buildResourceContext(data);
      const rowReadableNames = extractRowReadableNames(data);

      // Set loading state
      setCache(prev => ({
        ...prev,
        [uuid]: {
          loading: true,
          resourceContext,
        },
      }));

      // Fetch analysis
      try {
        const requestBody: {
          resourceType: string;
          resourceDisplays: string[];
          resourceRowData: unknown;
          context: string;
          suspectsContext?: string;
        } = {
          resourceType: tableType,
          resourceDisplays: rowReadableNames,
          resourceRowData: rowData,
          context: resourceContext,
        };

        // Add suspectsContext if provided
        if (suspectsContext) {
          requestBody.suspectsContext = suspectsContext;
        }

        const result = await apiOSS.post<{ summary: string }>(
          "/medical/v1/inference/resource/summary",
          requestBody
        );

        // Update cache with result
        setCache(prev => ({
          ...prev,
          [uuid]: {
            analysis: result.data.summary,
            loading: false,
            resourceContext,
          },
        }));
      } catch (err) {
        console.error("Failed to fetch resource analysis:", err);

        // Update cache with error
        setCache(prev => ({
          ...prev,
          [uuid]: {
            loading: false,
            error: "Failed to load analysis. Please try again.",
            resourceContext,
          },
        }));
      }
    },
    [bundle, cache, buildResourceContext, extractRowReadableNames, isEnabled]
  );

  return {
    cache,
    startAnalysis,
    buildCacheKey,
  };
}
